/**
 * /equation-solver — решение уравнения, неравенства или системы, а не ответ на них.
 *
 * Готовый ответ школьнику не помогает: списать можно, сдать нельзя — учитель
 * просит решение. Поэтому здесь на экране стоят ШАГИ, способ решения можно
 * выбрать (дискриминант или Виет, подстановка или сложение), а рядом —
 * проверка подстановкой, которой ученик убеждается сам, не веря нам на слово.
 *
 * ⚠️ Математика приходит из math-core готовыми строками, а объяснения — ключами
 * (`step_discriminant`), которые переводит страница. Иначе решатель говорил бы
 * по-русски во всех десяти локалях.
 *
 * ⚠️ Переменная берётся ИЗ ЗАПИСИ, а не предполагается «икс»: школьник пишет
 * «2y + 1 = 7» и «S = 3t», и отказ «введите уравнение с x» здесь был бы
 * отказом решать правильно записанное уравнение.
 */

import { parse, compile, evaluate, MathParseError, KNOWN_FUNCTIONS, KNOWN_CONSTANTS } from '../math-core/parse.js';
import { solveEquation } from '../math-core/solve.js';
import { ratOf, polySub, degree, frDiv } from '../math-core/poly.js';
import { samples, fmtNum, niceStep } from '../math-core/analyze.js';
import { isInequality, splitInequality, solveInequality, intersectSets, setText } from '../math-core/inequality.js';
import { splitSystem, solveSystem, normalizeSystem } from '../math-core/system.js';
import { quadraticMethods } from '../math-core/methods.js';
import { generate, rng } from '../math-core/practice.js';
import { readImage, equationLines, ocrAvailable } from './photo.js';
import { decimalSep, localizeMath, intervalSep } from './format.js';

const CFG = window.__ES_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'equation-solver';
const LOCALE = window.__LOCALE || 'en';
const $ = (id) => document.getElementById(id);
const t = (k, d) => I18N[k] || d || k;

const DEC = decimalSep(LOCALE);
const SEP = intervalSep(DEC);
const loc = (s) => localizeMath(s, DEC);

const state = {
    mode: 'equation', src: '', sys: ['', ''], res: null, parsed: null,
    varName: 'x', assumedZero: false, method: null, methods: null, fromPhoto: false, practice: null,
};

/* ── разбор ──────────────────────────────────────────────────────────────── */
const RESERVED = new Set(KNOWN_FUNCTIONS.concat(KNOWN_CONSTANTS).map((s) => s.toLowerCase()));

/**
 * Имена переменных приходится узнать ДО разбора: разборщик принимает список
 * переменных параметром и всё остальное считает ошибкой.
 *
 * ⚠️ Переменной считается ОДНА буква — именно так их и пишут. Многобуквенное
 * имя осталось ошибкой намеренно: иначе «sinx» стал бы переменной, и вместо
 * подсказки про скобки человек получил бы ответ на уравнение, которого не писал.
 */
function candidateVars(src) {
    const out = [];
    const re = /[A-Za-zА-Яа-яπ][A-Za-z0-9А-Яа-яπ_]*/g;
    let m;
    while ((m = re.exec(src))) {
        const low = m[0].toLowerCase();
        if (low.length !== 1 || RESERVED.has(low)) continue;
        if (out.indexOf(low) < 0) out.push(low);
    }
    return out;
}

function parseError(e, offset) {
    if (e instanceof MathParseError) {
        // ⚠️ `code` нужен телеметрии: он несёт НЕПОНЯТЫЙ символ (`unexpected_char:²`).
        return { msg: t('err_parse'), at: typeof e.at === 'number' ? e.at + offset : null, code: e.message };
    }
    return { msg: t('err_parse'), code: String((e && e.message) || 'unknown') };
}

/** Уравнение с одной переменной. Остаётся экспортом страницы: на нём стоят сторожа. */
function solveSource(src) {
    const clean = String(src || '').trim();
    if (!clean) return null;
    const parts = clean.split('=');
    if (parts.length > 2) return { error: { msg: t('err_many_eq') } };

    // Без знака равенства подразумевается «= 0»: так пишут в задачниках.
    const assumedZero = parts.length === 1;
    const leftSrc = parts[0];
    const rightSrc = assumedZero ? '0' : parts[1];
    if (!rightSrc.trim()) return { error: { msg: t('err_empty_side') } };

    const names = candidateVars(clean);
    if (names.length > 1) return { error: { msg: t('err_two_vars') + ' ' + names.join(', '), code: 'two_vars' } };
    const varName = names[0] || 'x';

    let left;
    let right;
    try { left = parse(leftSrc, { vars: [varName] }); } catch (e) { return { error: parseError(e, 0) }; }
    try { right = parse(rightSrc, { vars: [varName] }); } catch (e) { return { error: parseError(e, leftSrc.length + 1) }; }

    const res = solveEquation(left, right, varName);
    return { res, varName, assumedZero, left, right, kind: 'equation' };
}

/** Неравенство, в том числе двойное «−3 < 2x + 1 ≤ 5». */
function solveIneqSource(src) {
    const sp = splitInequality(src);
    if (!sp) return null;
    if (sp.parts.some((p) => !p.trim())) return { error: { msg: t('err_empty_side'), code: 'empty_side' } };
    if (sp.rels.length > 2 || sp.parts.some((p) => p.indexOf('=') >= 0)) return { error: { msg: t('err_many_eq'), code: 'many_rel' } };
    const names = candidateVars(src);
    if (names.length > 1) return { error: { msg: t('err_ineq_vars') + ' ' + names.join(', '), code: 'two_vars' } };
    const varName = names[0] || 'x';
    const asts = [];
    let offset = 0;
    for (const p of sp.parts) {
        try { asts.push(parse(p, { vars: [varName] })); } catch (e) { return { error: parseError(e, offset) }; }
        offset += p.length + 1;
    }
    let res = solveInequality(asts[0], asts[1], sp.rels[0], varName, { sep: SEP });
    if (sp.rels.length === 2) {
        const second = solveInequality(asts[1], asts[2], sp.rels[1], varName, { sep: SEP });
        const both = intersectSets(res, second);
        both.steps = res.steps.concat(second.steps);
        both.exact = res.exact && second.exact;
        both.range = res.range || second.range;
        both.kind = 'inequality';
        both.text = setText(both, varName, SEP);
        res = both;
    }
    return { res, varName, asts, rels: sp.rels, kind: 'inequality' };
}

/* ── запись для экрана ───────────────────────────────────────────────────── */
const SUPD = { 0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹' };
const REL = { '<=': '≤', '>=': '≥', '=<': '≤', '=>': '≥' };
function pretty(src) {
    return String(src || '')
        .replace(/<=|>=|=<|=>/g, (m) => REL[m])
        .replace(/\s*\^\s*(\d+)/g, (_, d) => d.split('').map((c) => SUPD[c]).join(''))
        .replace(/\*/g, '·')
        .replace(/-/g, '−')
        .replace(/\s*([=<>≤≥])\s*/g, ' $1 ')
        .replace(/\s+/g, ' ')
        .trim();
}
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const sub = (n) => String(n).split('').map((d) => SUB[+d]).join('');

/* ── режим ───────────────────────────────────────────────────────────────── */
function setMode(mode, opts = {}) {
    if (mode === state.mode && !opts.force) return;
    const prev = state.mode;
    state.mode = mode;
    document.querySelectorAll('[data-es-mode]').forEach((b) => {
        const on = b.getAttribute('data-es-mode') === mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const sys = mode === 'system';
    $('esSys').hidden = !sys;
    $('esSingle').hidden = sys;
    const input = $('esInput');
    input.placeholder = input.getAttribute(mode === 'inequality' ? 'data-ph-inequality' : 'data-ph-equation') || input.placeholder;
    document.querySelectorAll('[data-es-for]').forEach((b) => {
        const f = b.getAttribute('data-es-for');
        b.hidden = !!f && f.split(' ').indexOf(mode) < 0;
    });
    document.querySelectorAll('[data-es-sample-mode]').forEach((b) => {
        b.hidden = b.getAttribute('data-es-sample-mode') !== mode;
    });
    state.method = null;
    if (opts.fill !== false && !opts.keep) {
        // Переключили режим руками — в поле кладём пример этого режима: пустая
        // карточка ничего не объясняет, а пример показывает, как записывать.
        const first = document.querySelector('[data-es-sample-mode="' + mode + '"]');
        if (first) fillFrom(first.getAttribute('data-es-sample'), mode);
    }
    if (prev !== mode) ae('mode', { mode, auto: opts.auto ? 1 : 0 });
}

/** Положить запись в поля нужного режима. */
function fillFrom(src, mode) {
    if (mode === 'system') {
        const eqs = splitSystem(src);
        $('esSys1').value = eqs[0] || '';
        $('esSys2').value = eqs[1] || '';
    } else {
        $('esInput').value = src;
    }
}

/**
 * Режим следует за записью: человек набрал «<» — это неравенство, вставил
 * «x + y = 5; xy = 6» — это система. Заставлять сперва искать переключатель
 * значило бы отказывать в правильно записанной задаче.
 */
function followInput() {
    const src = $('esInput').value;
    if (state.mode !== 'system') {
        const eqs = splitSystem(src);
        if (eqs.length === 2 && eqs.every((e) => e.indexOf('=') >= 0 && !isInequality(e))) {
            setMode('system', { keep: true, auto: true });
            fillFrom(src, 'system');
            $('esInput').value = '';
            return;
        }
        if (isInequality(src) && state.mode !== 'inequality') setMode('inequality', { keep: true, auto: true });
        else if (!isInequality(src) && state.mode === 'inequality' && src.indexOf('=') >= 0) setMode('equation', { keep: true, auto: true });
    }
}

/* ── сборка ──────────────────────────────────────────────────────────────── */
function refresh(fromInput) {
    const err = $('esError');
    const card = $('esCard');
    let parsed;
    if (state.mode === 'system') {
        state.sys = [$('esSys1').value, $('esSys2').value];
        state.src = state.sys.map((s) => s.trim()).filter(Boolean).join('; ');
        parsed = solveSystemSource(state.sys);
    } else {
        state.src = $('esInput').value;
        parsed = isInequality(state.src) ? solveIneqSource(state.src) : solveSource(state.src);
    }

    if (!parsed) {
        err.hidden = true;
        showResult(false);
        return;
    }
    if (parsed.error) {
        err.textContent = parsed.error.msg
            + (parsed.error.at != null ? ' (' + t('err_at') + ' ' + (parsed.error.at + 1) + ')' : '');
        err.hidden = false;
        showResult(false);
        if (fromInput) {
            stage('error', { reason: badChar(parsed.error.code), at: parsed.error.at });
            ae('parse_failed', { mode: state.mode, ch: badChar(parsed.error.code), at: parsed.error.at, len: state.src.length });
        }
        return;
    }

    err.hidden = true;
    state.parsed = parsed;
    state.res = parsed.res;
    state.varName = parsed.varName;
    state.assumedZero = !!parsed.assumedZero;
    card.hidden = false;
    showResult(true);

    if (fromInput) stage('editing', {});
    stage('solved', { kind: parsed.res.kind || parsed.kind, deg: parsed.res.degree });
    $('esAssumed').hidden = !parsed.assumedZero;
    $('esEq').textContent = parsed.kind === 'system'
        ? state.sys.map(pretty).join('\n')
        : pretty(state.src) + (parsed.assumedZero ? ' = 0' : '');

    if (parsed.kind === 'system') renderSystem(parsed);
    else if (parsed.kind === 'inequality') renderInequality(parsed);
    else renderEquation(parsed);

    renderPlot();
    flash($('esAnswer'));
    saveHash();
    if (!fromInput) return;
    ae('solve', {
        mode: state.mode,
        kind: parsed.res.kind || parsed.kind,
        sub: parsed.res.sub || '',
        deg: parsed.res.degree,
        roots: (parsed.res.roots || parsed.res.solutions || parsed.res.segs || []).length,
        why: parsed.res.why || '',
        approx: isApprox(parsed) ? 1 : 0,
        photo: state.fromPhoto ? 1 : 0,
        len: state.src.length,
    });
}

function showResult(on) {
    $('esCard').hidden = !on;
    $('esStepsWrap').hidden = !on;
    $('esPlotWrap').hidden = !on;
}

function isApprox(p) {
    if (p.kind === 'inequality') return p.res.exact === false;
    if (p.kind === 'system') return p.res.sub === 'numeric';
    return (p.res.roots || []).some((r) => r.approx);
}

function flash(el) {
    if (!el) return;
    el.classList.remove('tc-flash');
    void el.offsetWidth;          // перезапуск анимации появления
    el.classList.add('tc-flash');
}

/* ── уравнение ───────────────────────────────────────────────────────────── */
function renderEquation(parsed) {
    const { res, varName: v } = parsed;
    const box = $('esAnswer');
    box.innerHTML = '';
    const line = document.createElement('p');
    line.className = 'es-ans es-mono';

    if (res.kind === 'identity') {
        line.textContent = t('ans_identity');
    } else if (!res.roots.length && res.complex) {
        line.innerHTML = '<span class="es-ans-lbl">' + esc(t('ans_complex')) + '</span>'
            + esc(v) + ' = ' + esc(loc(res.complex.re)) + ' ± ' + esc(loc(res.complex.im)) + 'i';
    } else if (!res.roots.length) {
        line.textContent = t('ans_none');
    } else {
        const many = res.roots.length > 1;
        line.innerHTML = res.roots.map((r, i) => {
            let s = esc((many ? v + sub(i + 1) : v) + ' = ' + loc(r.text));
            if (r.near) s += ' <small>≈ ' + esc(loc(r.near)) + '</small>';
            if (r.mult > 1) s += ' <small>(' + esc(t('ans_mult').replace(':n', r.mult)) + ')</small>';
            return s;
        }).join(',&nbsp;&nbsp; ');
    }
    box.appendChild(line);

    // Приближённый ответ обязан назвать себя приближённым — и сказать, где искали.
    if (res.roots.some((r) => r.approx)) {
        note(box, res.range
            ? t('note_numeric').replace(':a', '−' + res.range).replace(':b', String(res.range))
            : t('note_approx'));
    }
    if (res.extraneous && res.extraneous.length) {
        note(box, t('note_extraneous').replace(':roots', res.extraneous.map(loc).join(', ')));
    }

    // Способы: у квадратного — неполное / дискриминант / Виета / k / квадрат.
    let methods = null;
    if ((res.kind === 'quadratic' || res.kind === 'complex') && !res.steps.some((s) => s.key === 'step_domain')) {
        const rl = ratOf(parsed.left, v);
        const rr = ratOf(parsed.right, v);
        if (rl && rr && degree(rl.d) === 0 && degree(rr.d) === 0) {
            const p = polySub(rl.n.map((c) => frDiv(c, rl.d[0])), rr.n.map((c) => frDiv(c, rr.d[0])));
            if (degree(p) === 2) methods = quadraticMethods(p, res, v).map((m) => ({ id: m.id, steps: m.steps }));
        }
    }
    renderMethods(methods, res.steps);

    renderChecks((res.checks || []).map((c) => ({
        head: v + ' = ' + loc(c.root),
        body: '→ ' + loc(c.left) + ' = ' + loc(c.right),
        ok: c.ok,
    })), t('check_title'));
}

function note(box, text) {
    const p = document.createElement('p');
    p.className = 'es-note';
    p.textContent = text;
    box.appendChild(p);
}

/* ── неравенство ─────────────────────────────────────────────────────────── */
function renderInequality(parsed) {
    const { res, varName: v } = parsed;
    const box = $('esAnswer');
    box.innerHTML = '';
    const line = document.createElement('p');
    line.className = 'es-ans es-mono';
    if (res.text === '∅') line.textContent = t('ans_ineq_none');
    else if (res.text === 'ℝ') line.innerHTML = esc(v + ' ∈ ℝ') + ' <small>(' + esc(t('ans_ineq_all')) + ')</small>';
    else line.textContent = v + ' ∈ ' + loc(res.text);
    box.appendChild(line);
    if (res.exact === false) {
        note(box, t('note_ineq_numeric').replace(':a', '−' + (res.range || 50)).replace(':b', String(res.range || 50)));
    }
    renderMethods(null, res.steps);

    // Проверка точкой из каждого промежутка ответа: подставить и увидеть знак.
    const rows = [];
    for (const sg of (res.segs || []).slice(0, 4)) {
        const x = probePoint(sg);
        if (x == null) continue;
        const vals = parsed.asts.map((a) => evaluate(a, { [v]: x }));
        if (!vals.every(Number.isFinite)) continue;
        const ok = parsed.rels.every((rel, i) => cmp(vals[i], vals[i + 1], rel));
        let body = '→ ' + loc(num6(vals[0]));
        parsed.rels.forEach((rel, i) => { body += ' ' + rel + ' ' + loc(num6(vals[i + 1])); });
        rows.push({ head: v + ' = ' + loc(num6(x)), body, ok });
    }
    renderChecks(rows, t('check_title_ineq'));
}

/** Удобная точка внутри промежутка: целая, если такая есть. */
function probePoint(sg) {
    const lo = sg.lo.p ? sg.lo.p.value : null;
    const hi = sg.hi.p ? sg.hi.p.value : null;
    if (lo != null && hi != null && lo === hi) return lo;
    if (lo == null && hi == null) return 0;
    if (lo == null) return Math.floor(hi) - (Number.isInteger(hi) ? 1 : 0);
    if (hi == null) return Math.ceil(lo) + (Number.isInteger(lo) ? 1 : 0);
    const inside = (x) => (x > lo && x < hi) || (x === lo && sg.lo.closed) || (x === hi && sg.hi.closed);
    for (const x of [Math.round((lo + hi) / 2), Math.ceil(lo), Math.floor(hi)]) if (inside(x)) return x;
    return (lo + hi) / 2;
}
function cmp(a, b, rel) {
    const e = 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
    if (rel === '<') return a < b - e;
    if (rel === '>') return a > b + e;
    if (rel === '≤') return a <= b + e;
    return a >= b - e;
}
const num6 = (x) => {
    const r = Math.round(x * 1e6) / 1e6;
    return String(r === 0 ? 0 : r).replace('-', '−');
};

/* ── система ─────────────────────────────────────────────────────────────── */
function solveSystemSource(srcs) {
    const eqs = srcs.map((s) => String(s || '').trim()).filter(Boolean);
    if (!eqs.length) return null;
    if (eqs.length === 1) return null;              // второе уравнение ещё не набрано
    const res = solveSystem(eqs);
    if (res.error) {
        const map = { sys_count: 'err_sys_count', sys_vars: 'err_sys_vars', sys_nonlinear: 'err_sys_nonlinear', sys_eq: 'err_sys_eq', sys_parse: 'err_parse' };
        let msg = t(map[res.error] || 'err_parse');
        if (res.error === 'sys_vars') msg += ' ' + (res.vars || []).join(', ');
        if (res.error === 'sys_parse' || res.error === 'sys_eq') msg += ' (' + (res.index + 1) + ')';
        return { error: { msg, code: res.error === 'sys_parse' ? res.code : res.error } };
    }
    return { res, varName: res.vars.join(','), kind: 'system' };
}

function renderSystem(parsed) {
    const { res } = parsed;
    const vars = res.vars;
    const box = $('esAnswer');
    box.innerHTML = '';
    const line = document.createElement('p');
    line.className = 'es-ans es-mono';
    if (res.sub === 'none') line.textContent = t('ans_sys_none');
    else if (res.sub === 'infinite') line.innerHTML = '<span class="es-ans-lbl">' + esc(t('ans_sys_infinite')) + '</span>' + esc(loc(res.param || ''));
    else if (!res.solutions.length) line.textContent = t('ans_sys_none');
    else {
        const pair = (s) => '(' + vars.map((v) => loc(s[v].text) + (s[v].near ? ' ≈ ' + loc(s[v].near) : '')).join(SEP) + ')';
        line.innerHTML = '<span class="es-ans-lbl">(' + esc(vars.join(SEP)) + ')</span>'
            + res.solutions.map((s) => esc(pair(s))).join(',&nbsp;&nbsp; ');
    }
    box.appendChild(line);
    if (res.sub === 'numeric' && res.range) {
        note(box, t('note_numeric').replace(':a', '−' + res.range).replace(':b', String(res.range)));
    }
    const methods = res.order.map((id) => ({ id, steps: res.methods[id] }));
    renderMethods(methods.length > 1 ? methods : null, methods[0].steps);

    renderChecks((res.checks || []).map((pairChecks, i) => {
        const s = res.solutions[i];
        return {
            head: '(' + vars.map((v) => loc(s[v].near || s[v].text)).join(SEP) + ')',
            body: pairChecks.map((c) => loc(c.left) + ' = ' + loc(c.right)).join(';   '),
            ok: pairChecks.every((c) => c.ok),
        };
    }), t('check_title'));
}

/* ── способы и шаги ──────────────────────────────────────────────────────── */
function renderMethods(methods, fallbackSteps) {
    const bar = $('esMethods');
    state.methods = methods;
    if (!methods || methods.length < 2) {
        bar.hidden = true;
        bar.innerHTML = '';
        renderSteps((methods && methods[0] && methods[0].steps) || fallbackSteps);
        return;
    }
    if (!state.method || !methods.some((m) => m.id === state.method)) state.method = methods[0].id;
    bar.innerHTML = '';
    for (const m of methods) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-seg-b' + (m.id === state.method ? ' on' : '');
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', m.id === state.method ? 'true' : 'false');
        b.setAttribute('data-es-method', m.id);
        b.textContent = t('method_' + m.id, m.id);
        bar.appendChild(b);
    }
    bar.hidden = false;
    renderSteps(methods.find((m) => m.id === state.method).steps);
}

function pickMethod(id) {
    if (!state.methods || id === state.method) return;
    const m = state.methods.find((x) => x.id === id);
    if (!m) return;
    state.method = id;
    const apply = () => {
        document.querySelectorAll('[data-es-method]').forEach((b) => {
            const on = b.getAttribute('data-es-method') === id;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        renderSteps(m.steps);
    };
    if (window.__viewTransition) window.__viewTransition(apply); else apply();
    saveHash();
    ae('method', { id, mode: state.mode });
}

function renderSteps(steps) {
    const list = $('esSteps');
    list.innerHTML = '';
    (steps || []).forEach((st) => {
        const li = document.createElement('li');
        const why = document.createElement('span');
        why.className = 'es-why';
        why.textContent = t(st.key, st.key);
        const math = document.createElement('code');
        math.className = 'es-math es-mono';
        math.textContent = loc(st.pretty ? pretty(st.text) : st.text);
        li.appendChild(why);
        li.appendChild(math);
        list.appendChild(li);
    });
}

function renderChecks(rows, title) {
    const wrap = $('esCheckWrap');
    const body = $('esCheck');
    body.innerHTML = '';
    $('esCheckTitle').textContent = title;
    if (!rows.length) { wrap.hidden = true; return; }
    for (const r of rows) {
        const row = document.createElement('div');
        row.className = 'es-chk' + (r.ok ? '' : ' bad');
        row.innerHTML = '<code class="es-mono">' + esc(r.head) + '</code><span class="es-mono">' + esc(r.body) + '</span><b>' + (r.ok ? '✓' : '✗') + '</b>';
        body.appendChild(row);
    }
    wrap.hidden = false;
}

/* ── график ──────────────────────────────────────────────────────────────── */
const PALETTE = ['#4f9cf9', '#f97362'];

function renderPlot() {
    const parsed = state.parsed;
    const wrap = $('esPlotWrap');
    const cv = $('esPlot');
    if (!parsed || !parsed.res || wrap.hidden) return;
    const legend = $('esLegend');
    legend.hidden = true;

    // ⚠️ Панель уже показана ДО замера: холст в скрытой панели даёт коробку 0×0,
    // битмап 0×0 — на экране пусто, а отчёт PDF падает целиком. Пол — ниже.
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const rect = cv.getBoundingClientRect();
    const w = Math.max(280, Math.round(rect.width || 640));
    const h = Math.max(160, Math.round(rect.height || 220));
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const css = getComputedStyle(document.documentElement);
    const ink = (css.getPropertyValue('--text-secondary') || '#9aa4b2').trim();
    const grid = (css.getPropertyValue('--border') || '#2a3441').trim();
    const accent = (css.getPropertyValue('--accent') || PALETTE[0]).trim() || PALETTE[0];
    const colors = { ink, grid, accent };

    if (parsed.kind === 'system') {
        $('esPlotTitle').textContent = t('plot_title_sys');
        cv.setAttribute('aria-label', t('plot_label_sys'));
        plotSystem(g, w, h, parsed, colors);
        return;
    }
    const v = parsed.varName;
    let f;
    try {
        const A = parsed.kind === 'inequality' ? parsed.asts[0] : parsed.left;
        const B = parsed.kind === 'inequality' ? parsed.asts[parsed.asts.length - 1] : parsed.right;
        if (parsed.kind === 'inequality' && parsed.asts.length === 3) {
            // Двойное: на прямой важен ответ, кривая — средняя часть относительно краёв.
            const fm = compile(parsed.asts[1], v);
            const fa = compile(parsed.asts[0], v);
            f = (x) => fm(x) - fa(x);
        } else {
            const fl = compile(A, v);
            const fr = compile(B, v);
            f = (x) => fl(x) - fr(x);
        }
    } catch (_) { return; }

    const marks = parsed.kind === 'inequality'
        ? (parsed.res.points || []).map((p) => p.p.value).concat((parsed.res.segs || []).flatMap((s) => [s.lo.p, s.hi.p].filter(Boolean).map((p) => p.value)))
        : parsed.res.roots.map((r) => r.value);
    const pts = marks.filter(Number.isFinite);
    let x0 = -10;
    let x1 = 10;
    if (pts.length) {
        const lo = Math.min.apply(null, pts);
        const hi = Math.max.apply(null, pts);
        const pad = Math.max(2, (hi - lo) * 0.6);
        x0 = lo - pad;
        x1 = hi + pad;
    }

    // Вертикальное окно — по значениям на видимом отрезке с отбрасыванием
    // выбросов: одна асимптота иначе прижимает всю кривую к оси.
    const all = samples(f, x0, x1, w, { yLimit: 1e9, yView: 1e9 });
    const ys = all.filter(Boolean).map((p) => p.y).sort((a, b) => a - b);
    const cut = Math.floor(ys.length * 0.02);
    const lo = ys.length ? ys[cut] : -5;
    const hi = ys.length ? ys[ys.length - 1 - cut] : 5;
    const span = Math.max(1e-6, hi - lo);
    let y0 = lo - span * 0.15;
    let y1 = hi + span * 0.15;
    if (y0 > 0) y0 = -span * 0.1;
    if (y1 < 0) y1 = span * 0.1;
    const view = { x0, x1, y0, y1, w, h };

    if (parsed.kind === 'inequality') {
        $('esPlotTitle').textContent = t('plot_title_ineq');
        cv.setAttribute('aria-label', t('plot_label_ineq'));
        // Ответ — полосой по всей высоте: видно, где кривая «выше» или «ниже» нуля.
        g.fillStyle = hexA(accent, 0.14);
        for (const sg of parsed.res.segs || []) {
            const a = sg.lo.p ? X(view, sg.lo.p.value) : 0;
            const b = sg.hi.p ? X(view, sg.hi.p.value) : w;
            g.fillRect(a, 0, Math.max(3, b - a), h);
        }
    } else {
        $('esPlotTitle').textContent = t('plot_title');
        cv.setAttribute('aria-label', t('plot_label'));
    }

    drawGrid(g, view, colors);
    const curve = samples(f, x0, x1, w, { yLimit: 1e6, yView: (y1 - y0) / 2 });
    g.strokeStyle = accent;
    g.lineWidth = 2;
    g.beginPath();
    let pen = false;
    for (const p of curve) {
        if (!p) { pen = false; continue; }
        const px = X(view, p.x);
        const py = Y(view, p.y);
        if (!pen) { g.moveTo(px, py); pen = true; } else g.lineTo(px, py);
    }
    g.stroke();

    g.font = '12px system-ui, sans-serif';
    if (parsed.kind === 'inequality') {
        const seen = new Set();
        for (const sg of parsed.res.segs || []) {
            for (const [end, closed] of [[sg.lo.p, sg.lo.closed], [sg.hi.p, sg.hi.closed]]) {
                if (!end || seen.has(end.value)) continue;
                seen.add(end.value);
                endpoint(g, X(view, end.value), Y(view, 0), closed, accent, ink, w, h, loc(end.text));
            }
        }
        for (const p of parsed.res.points || []) {
            if (seen.has(p.p.value)) continue;
            seen.add(p.p.value);
            endpoint(g, X(view, p.p.value), Y(view, 0), false, grid, ink, w, h, loc(p.p.text));
        }
    } else {
        for (const r of parsed.res.roots) {
            if (!Number.isFinite(r.value)) continue;
            endpoint(g, X(view, r.value), Y(view, 0), true, accent, ink, w, h, loc(fmtNum(r.value, 3)));
        }
    }
}

const X = (vw, x) => (x - vw.x0) / (vw.x1 - vw.x0) * vw.w;
const Y = (vw, y) => vw.h - (y - vw.y0) / (vw.y1 - vw.y0) * vw.h;

function drawGrid(g, vw, c) {
    g.save();
    g.lineWidth = 1;
    g.strokeStyle = hexA(c.grid, 0.45);
    g.fillStyle = c.ink;
    g.font = '11px system-ui, sans-serif';
    const sx = niceStep(vw.x1 - vw.x0, vw.w, 70);
    const sy = niceStep(vw.y1 - vw.y0, vw.h, 44);
    g.beginPath();
    for (let x = Math.ceil(vw.x0 / sx) * sx; x <= vw.x1; x += sx) { const px = Math.round(X(vw, x)) + 0.5; g.moveTo(px, 0); g.lineTo(px, vw.h); }
    for (let y = Math.ceil(vw.y0 / sy) * sy; y <= vw.y1; y += sy) { const py = Math.round(Y(vw, y)) + 0.5; g.moveTo(0, py); g.lineTo(vw.w, py); }
    g.stroke();
    g.strokeStyle = c.grid;
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(0, Y(vw, 0)); g.lineTo(vw.w, Y(vw, 0));
    g.moveTo(X(vw, 0), 0); g.lineTo(X(vw, 0), vw.h);
    g.stroke();
    // Подписи делений по нижнему краю: окно графика без чисел ничего не говорит о масштабе.
    g.globalAlpha = 0.75;
    for (let x = Math.ceil(vw.x0 / sx) * sx; x <= vw.x1; x += sx) {
        if (Math.abs(x) < sx / 2) continue;
        const label = loc(num6(x));
        const tw = g.measureText(label).width;
        g.fillText(label, Math.min(vw.w - tw - 2, Math.max(2, X(vw, x) - tw / 2)), vw.h - 4);
    }
    g.restore();
}

function endpoint(g, px, py, filled, color, ink, w, h, label) {
    if (px < -4 || px > w + 4) return;
    const y = Math.min(h - 6, Math.max(6, py));
    g.beginPath();
    g.arc(px, y, 5, 0, Math.PI * 2);
    if (filled) { g.fillStyle = color; g.fill(); }
    else { g.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--bg-primary') || '#111'; g.fill(); g.lineWidth = 2; g.strokeStyle = color; g.stroke(); }
    g.fillStyle = ink;
    const tw = g.measureText(label).width;
    g.fillText(label, Math.min(w - tw - 2, Math.max(2, px - tw / 2)), y + (y > h - 26 ? -10 : 18));
}

/** Кривые F(x, y) = 0 обоих уравнений — марширующими квадратами по сетке. */
function plotSystem(g, w, h, parsed, colors) {
    const res = parsed.res;
    const vars = res.vars;
    const fns = [];
    for (const src of res.srcs) {
        const parts = src.split('=');
        try {
            const L = parse(parts[0], { vars });
            const R = parse(parts[1], { vars });
            fns.push((x, y) => evaluate(L, { [vars[0]]: x, [vars[1]]: y }) - evaluate(R, { [vars[0]]: x, [vars[1]]: y }));
        } catch (_) { fns.push(null); }
    }
    const sols = (res.solutions || []).filter((s) => Number.isFinite(s[vars[0]].value) && Number.isFinite(s[vars[1]].value));
    let cx = 0;
    let cy = 0;
    let half = 8;
    if (sols.length) {
        const xs = sols.map((s) => s[vars[0]].value);
        const yv = sols.map((s) => s[vars[1]].value);
        const minX = Math.min.apply(null, xs);
        const maxX = Math.max.apply(null, xs);
        const minY = Math.min.apply(null, yv);
        const maxY = Math.max.apply(null, yv);
        cx = (minX + maxX) / 2;
        cy = (minY + maxY) / 2;
        half = Math.max(4, (maxX - minX) * 0.9, (maxY - minY) * 0.9 * (w / h));
    }
    // Масштаб одинаковый по осям: окружность должна остаться окружностью.
    const vw = { x0: cx - half, x1: cx + half, w, h };
    const yHalf = half * h / w;
    vw.y0 = cy - yHalf;
    vw.y1 = cy + yHalf;
    drawGrid(g, vw, colors);

    const nx = Math.max(60, Math.round(w / 3));
    const ny = Math.max(40, Math.round(h / 3));
    fns.forEach((F, idx) => {
        if (!F) return;
        const vals = new Float64Array((nx + 1) * (ny + 1));
        for (let j = 0; j <= ny; j++) {
            const y = vw.y1 - (j / ny) * (vw.y1 - vw.y0);
            for (let i = 0; i <= nx; i++) vals[j * (nx + 1) + i] = F(vw.x0 + (i / nx) * (vw.x1 - vw.x0), y);
        }
        g.strokeStyle = idx === 0 ? colors.accent : PALETTE[1];
        g.lineWidth = 2;
        g.beginPath();
        const cw = w / nx;
        const ch = h / ny;
        for (let j = 0; j < ny; j++) {
            for (let i = 0; i < nx; i++) {
                const a = vals[j * (nx + 1) + i];
                const b = vals[j * (nx + 1) + i + 1];
                const c = vals[(j + 1) * (nx + 1) + i + 1];
                const d = vals[(j + 1) * (nx + 1) + i];
                if (![a, b, c, d].every(Number.isFinite)) continue;
                const pts = [];
                const edge = (va, vb, x0, y0, x1, y1) => {
                    if ((va < 0) === (vb < 0)) return;
                    // Смена знака через разрыв (xy = 6 у оси) — не кривая: пропускаем.
                    if (Math.abs(va - vb) > 50 * (Math.abs(va) + Math.abs(vb)) / 2 + 1e3) return;
                    const k = va / (va - vb);
                    pts.push([x0 + (x1 - x0) * k, y0 + (y1 - y0) * k]);
                };
                const px = i * cw;
                const py = j * ch;
                edge(a, b, px, py, px + cw, py);
                edge(b, c, px + cw, py, px + cw, py + ch);
                edge(d, c, px, py + ch, px + cw, py + ch);
                edge(a, d, px, py, px, py + ch);
                for (let k = 0; k + 1 < pts.length; k += 2) {
                    g.moveTo(pts[k][0], pts[k][1]);
                    g.lineTo(pts[k + 1][0], pts[k + 1][1]);
                }
            }
        }
        g.stroke();
    });
    g.font = '12px system-ui, sans-serif';
    for (const s of sols) {
        const label = '(' + vars.map((v) => loc(num6(s[v].value))).join(SEP) + ')';
        endpoint(g, X(vw, s[vars[0]].value), Y(vw, s[vars[1]].value), true, colors.ink, colors.ink, w, h, label);
    }
    const legend = $('esLegend');
    legend.innerHTML = res.srcs.map((s, i) => '<span><i style="background:' + (i === 0 ? esc(colors.accent) : PALETTE[1]) + '"></i>' + esc(pretty(s)) + '</span>').join('');
    legend.hidden = false;
}

function hexA(color, a) {
    const m = /^#([0-9a-f]{6})$/i.exec(String(color).trim());
    if (!m) return color;
    const n = parseInt(m[1], 16);
    return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
}

/* ── адресная строка: решение передаётся ссылкой ─────────────────────────── */
function saveHash() {
    const q = new URLSearchParams();
    if (state.mode === 'system') q.set('s', state.sys.map((s) => s.trim()).join(';'));
    else q.set('e', state.src);
    if (state.method && state.methods && state.method !== state.methods[0].id) q.set('m', state.method);
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    if (q.get('m')) state.method = q.get('m');
    const s = q.get('s');
    if (s) {
        setMode('system', { keep: true, force: true });
        fillFrom(s, 'system');
        return true;
    }
    const e = q.get('e');
    if (!e) return false;
    const val = safeDecode(e);
    setMode(isInequality(val) ? 'inequality' : 'equation', { keep: true, force: true });
    $('esInput').value = val;
    return true;
}
/** Старые ссылки кодировали уравнение ещё раз поверх URLSearchParams. */
function safeDecode(s) {
    try { return /%[0-9A-F]{2}/i.test(s) ? decodeURIComponent(s) : s; } catch (_) { return s; }
}

/* ── выдача ──────────────────────────────────────────────────────────────── */
/**
 * Отчёт собирается отдельным узлом, а не снимается со страницы: ответ, шаги и
 * график теперь живут в разных панелях, а лист решения должен идти одним
 * столбцом — условие, ответ, способ, шаги, проверка, график.
 */
function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app es es-report';
    root.style.cssText = 'padding:0;background:#fff;max-width:none';
    const add = (tag, cls, text) => {
        const el = document.createElement(tag);
        if (cls) el.className = cls;
        if (text != null) el.textContent = text;
        root.appendChild(el);
        return el;
    };
    add('p', 'es-eq es-mono', $('esEq').textContent).style.whiteSpace = 'pre-line';
    root.appendChild($('esAnswer').cloneNode(true)).removeAttribute('id');
    const methodBtn = document.querySelector('[data-es-method].on');
    add('p', 'es-chk-h', t('steps_title') + (methodBtn ? ' · ' + methodBtn.textContent : '')).style.marginTop = '14px';
    const steps = $('esSteps').cloneNode(true);
    steps.removeAttribute('id');
    root.appendChild(steps);
    if (!$('esCheckWrap').hidden) {
        add('p', 'es-chk-h', $('esCheckTitle').textContent).style.marginTop = '14px';
        const ch = $('esCheck').cloneNode(true);
        ch.removeAttribute('id');
        root.appendChild(ch);
    }
    const src = $('esPlot');
    if (src.width && src.height && !$('esPlotWrap').hidden) {
        add('p', 'es-chk-h', $('esPlotTitle').textContent).style.marginTop = '14px';
        const cv = document.createElement('canvas');
        cv.width = src.width;
        cv.height = src.height;
        cv.style.cssText = 'width:100%;max-width:640px;height:auto;display:block;border:1px solid #d6dde8;border-radius:10px';
        try { cv.getContext('2d').drawImage(src, 0, 0); } catch (_) { /* пустой холст лучше, чем упавший отчёт */ }
        root.appendChild(cv);
    }
    return root;
}

function savePdf() {
    if ($('esCard').hidden || !window.__generatePDF) return;
    const btn = $('esPdf');
    if (btn) btn.disabled = true;
    window.__generatePDF({
        element: buildReport(),
        title: t('pdf_title'),
        filename: pdfName(state.src || 'equation'),
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        if (btn) btn.disabled = false;
        ae('pdf', { ok: !!(r && r.ok), code: r && r.code, mode: state.mode });
        // __aeToolComplete сам ставит стадию «complete» — свой вызов был бы вторым.
        if (r && r.ok) {
            try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'pdf', kind: state.res && (state.res.kind || state.mode) }); } catch (_) {}
        }
    }, () => { if (btn) btn.disabled = false; });
}

/**
 * Решение простым текстом — то, что переписывают в тетрадь и шлют в чат.
 * Собирается из ТОГО ЖЕ, что на экране: расхождение текста и картинки было бы
 * отдельным сортом вранья.
 */
function solutionText() {
    const lines = [];
    const eq = $('esEq').textContent.trim();
    if (eq) lines.push(eq);
    const ans = Array.from($('esAnswer').children).map((el) => el.textContent.replace(/\s+/g, ' ').trim()).filter(Boolean);
    if (ans.length) lines.push('', ...ans);
    const steps = Array.from(document.querySelectorAll('#esSteps li'));
    if (steps.length) {
        const methodBtn = document.querySelector('[data-es-method].on');
        lines.push('', t('steps_title') + (methodBtn ? ' (' + methodBtn.textContent + ')' : '') + ':');
        steps.forEach((li, i) => {
            const why = (li.querySelector('.es-why') || {}).textContent || '';
            const math = (li.querySelector('.es-math') || {}).textContent || '';
            lines.push((i + 1) + '. ' + why.trim() + '  ' + math.trim());
        });
    }
    const checks = Array.from(document.querySelectorAll('#esCheck .es-chk'));
    if (checks.length && !$('esCheckWrap').hidden) {
        lines.push('', $('esCheckTitle').textContent + ':');
        // ⚠️ textContent склеивает соседние ячейки без пробела — «x = 2→ 0 = 0✓».
        checks.forEach((c) => {
            const part = (sel) => ((c.querySelector(sel) || {}).textContent || '').replace(/\s+/g, ' ').trim();
            lines.push('   ' + [part('code'), part('span'), part('b')].filter(Boolean).join(' '));
        });
    }
    return lines.join('\n');
}

/*
 * Решение → Markdown для «В презентацию»: название колоды — условие, слайды — шаги (пункт на шаг),
 * ответ, проверка. Конструктор раскладывает его тем же разбором, что «Открыть файл»
 * (presentation/import/outline.js), — без ИИ и без токенов.
 */
function solutionMarkdown() {
    const eq = $('esEq').textContent.replace(/\s+/g, ' ').trim();
    if (!eq) return '';
    const md = ['# ' + t('slides_deck'), ''];
    const steps = Array.from(document.querySelectorAll('#esSteps li'));
    if (steps.length) {
        const methodBtn = document.querySelector('[data-es-method].on');
        md.push('## ' + t('steps_title') + (methodBtn ? ' (' + methodBtn.textContent.trim() + ')' : ''), '');
        steps.forEach((li) => {
            const why = ((li.querySelector('.es-why') || {}).textContent || '').replace(/\s+/g, ' ').trim();
            const math = ((li.querySelector('.es-math') || {}).textContent || '').replace(/\s+/g, ' ').trim();
            const line = [why, math].filter(Boolean).join(': ');
            if (line) md.push('- ' + line);
        });
        md.push('');
    }
    const ans = Array.from($('esAnswer').children).map((el) => el.textContent.replace(/\s+/g, ' ').trim()).filter(Boolean);
    if (ans.length) { md.push('## ' + t('slides_answer'), ''); ans.forEach((a) => md.push('- ' + a)); md.push(''); }
    const checks = Array.from(document.querySelectorAll('#esCheck .es-chk'));
    if (checks.length && !$('esCheckWrap').hidden) {
        md.push('## ' + $('esCheckTitle').textContent.trim(), '');
        checks.forEach((c) => {
            const part = (sel) => ((c.querySelector(sel) || {}).textContent || '').replace(/\s+/g, ' ').trim();
            const line = [part('code'), part('span'), part('b')].filter(Boolean).join(' ');
            if (line) md.push('- ' + line);
        });
    }
    return md.join('\n');
}

/* «В презентацию»: Markdown решения — конструктору через localStorage (pm_import, 10 мин), страница
   решения остаётся открытой, конструктор открывается рядом. */
function toSlides() {
    const md = solutionMarkdown();
    if (!md || !CFG.slidesUrl) return;
    const eq = $('esEq').textContent.replace(/\s+/g, ' ').trim();
    try { localStorage.setItem('pm_import', JSON.stringify({ v: 1, from: 'equation-solver', at: Date.now(), name: t('slides_deck'), subtitle: eq, deckTitle: t('slides_deck') + ' — ' + eq, text: md })); }
    catch (e) { if (window.__toast) window.__toast(t('slides_failed'), 'error'); return; }
    try { if (window.__ae) window.__ae('tool_action', { slug: 'equation-solver', action: 'to_slides' }); } catch (e) { /* телеметрия */ }
    const w = window.open(CFG.slidesUrl, '_blank');
    if (!w) location.href = CFG.slidesUrl;
}

async function copySolution() {
    const text = solutionText();
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        if (window.__toast) window.__toast(t('copied'), 'success');
        ae('copy', { len: text.length, mode: state.mode });
    } catch (_) {
        if (window.__toast) window.__toast(t('copy_failed'), 'error');
        ae('copy_failed', {});
    }
}

/**
 * Поделиться: ссылка несёт саму задачу. На телефоне — системный лист
 * (мессенджер одноклассникам), где его нет — ссылка в буфер.
 */
async function share() {
    saveHash();
    const url = location.href;
    if (navigator.share) {
        try {
            await navigator.share({ title: document.title, text: pretty(state.src), url });
            ae('share', { via: 'native', mode: state.mode });
            return;
        } catch (e) {
            if (e && e.name === 'AbortError') return;       // человек передумал — это не ошибка
        }
    }
    try {
        await navigator.clipboard.writeText(url);
        if (window.__toast) window.__toast(t('link_copied'), 'success');
        ae('share', { via: 'clipboard', mode: state.mode });
    } catch (_) {
        if (window.__toast) window.__toast(t('copy_failed'), 'error');
    }
}

function pdfName(base) {
    const b = String(base).replace(/\s+/g, '').replace(/;/g, '_').slice(0, 40) || 'equation';
    try {
        if (window.__outputName) return window.__outputName.join(window.__outputName.sanitize(b), 'pdf');
    } catch (_) { /* примитив не загрузился — имя ниже */ }
    return 'equation.pdf';
}

/**
 * Открыть то же в построителе графиков: там окно двигается и читаются значения.
 * Контракт построителя — `#f=выражение;выражение`. Уравнение уходит разностью
 * частей, неравенство — тоже, система — каждое уравнение, выраженное через y,
 * если это возможно (окружность функцией от x не записывается — её не шлём).
 */
function plotUrl() {
    const base = CFG.plotterUrl || '';
    if (state.mode === 'system' && state.parsed && state.parsed.res && state.parsed.res.srcs) {
        const res = state.parsed.res;
        const list = res.srcs.map((s) => explicitY(s, res.vars)).filter(Boolean);
        return list.length ? base + '#f=' + list.map(encodeURIComponent).join(';') : '';
    }
    const src = state.src;
    const sp = splitInequality(src);
    const parts = sp ? sp.parts : src.split('=');
    const expr = parts.length >= 2 ? '(' + parts[0].trim() + ') - (' + parts[1].trim() + ')' : parts[0].trim();
    return base + '#f=' + encodeURIComponent(expr.replace(/\s+/g, ''));
}

/** F(x, y) = A(x) + B(x)·y  →  y = −A/B, если уравнение линейно по y. */
function explicitY(src, vars) {
    const [vx, vy] = vars;
    const parts = src.split('=');
    const re = new RegExp('(^|[^A-Za-zА-Яа-я])' + vy + '(?![A-Za-zА-Яа-я])', 'g');
    const at = (k) => parts.map((p) => p.replace(re, (m, pre) => pre + '(' + k + ')'));
    const D = (k) => { const [l, r] = at(k); return '((' + l + ')-(' + r + '))'; };
    try {
        const env = (x, y) => ({ [vx]: x, [vy]: y });
        const L = parse(parts[0], { vars });
        const R = parse(parts[1], { vars });
        const F = (x, y) => evaluate(L, env(x, y)) - evaluate(R, env(x, y));
        for (const x of [-2.3, 0.7, 3.1]) {
            const a = F(x, 0);
            const b = F(x, 1) - a;
            if (!Number.isFinite(a) || !Number.isFinite(b) || Math.abs(F(x, 3) - (a + 3 * b)) > 1e-7 * Math.max(1, Math.abs(a), Math.abs(b))) return null;
        }
        const expr = '-' + D(0) + '/(' + D(1) + '-' + D(0) + ')';
        return vx === 'x' ? expr.replace(/\s+/g, '') : expr.replace(new RegExp('(^|[^A-Za-z])' + vx + '(?![A-Za-z])', 'g'), '$1x').replace(/\s+/g, '');
    } catch (_) {
        return null;
    }
}

/* ── тренажёр ────────────────────────────────────────────────────────────── */
function currentKind() {
    const p = state.parsed;
    if (state.mode === 'system') return p && p.res && p.res.sub === 'linear' ? 'system_linear' : (p ? 'system_nonlinear' : 'system_linear');
    if (state.mode === 'inequality') {
        if (/\//.test(state.src)) return 'ineq_rational';
        return /\^|²/.test(state.src) ? 'ineq_quadratic' : 'ineq_linear';
    }
    const r = p && p.res;
    if (!r) return 'quadratic';
    if (r.steps.some((s) => s.key === 'step_domain')) return 'rational';
    if (r.kind === 'linear') return 'linear';
    if (r.kind === 'biquadratic') return 'biquadratic';
    if (r.kind === 'factored') return 'cubic';
    if (state.methods && state.methods[0] && state.methods[0].id === 'incomplete') return 'incomplete';
    return 'quadratic';
}

function seed() {
    try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch (_) { return Math.floor(Math.random() * 4294967295); }
}

function newPractice() {
    const kind = currentKind();
    const g = generate(kind, rng(seed()));
    state.practice = { src: g.src, mode: g.mode, kind };
    const task = $('esTask');
    task.textContent = g.mode === 'system' ? splitSystem(g.src).map(pretty).join('\n') : pretty(g.src);
    task.style.whiteSpace = 'pre-line';
    task.hidden = false;
    const canCheck = g.mode !== 'inequality';
    $('esPracticeAnswer').hidden = !canCheck;
    $('esPracticeAnswer').value = '';
    $('esPracticeCheck').hidden = !canCheck;
    $('esPracticeShow').hidden = false;
    const msg = $('esPracticeMsg');
    msg.hidden = canCheck;
    msg.className = 'es-practice-msg';
    msg.textContent = canCheck ? '' : t('practice_ineq_hint');
    ae('practice_new', { kind });
}

/** Ответ ученика: «2; 3», «x = 2, x = 3», «1/2», «(2; 3)». */
function readNumbers(text) {
    const cleaned = String(text || '')
        .replace(/[A-Za-zА-Яа-я][₀-₉0-9]*\s*=/g, ' ')
        .replace(/[()[\]{}]/g, ' ')
        .replace(/−/g, '-');
    return cleaned.split(/;|\s+|,\s+/).map((s) => s.trim()).filter(Boolean).map((s) => {
        try { return evaluate(parse(s, { vars: [] }), {}); } catch (_) { return NaN; }
    });
}

function checkPractice() {
    const p = state.practice;
    if (!p) return;
    const msg = $('esPracticeMsg');
    const nums = readNumbers($('esPracticeAnswer').value);
    msg.hidden = false;
    if (!nums.length || nums.some((n) => !Number.isFinite(n))) {
        msg.className = 'es-practice-msg bad';
        msg.textContent = t('practice_empty');
        return;
    }
    let ok = false;
    const near = (a, b) => Math.abs(a - b) < 1e-6 * Math.max(1, Math.abs(b));
    if (p.mode === 'system') {
        const r = solveSystem(splitSystem(p.src));
        const want = (r.solutions || []).map((s) => r.vars.map((v) => s[v].value));
        const got = [];
        for (let i = 0; i + 1 < nums.length; i += 2) got.push([nums[i], nums[i + 1]]);
        ok = nums.length % 2 === 0 && got.length === want.length
            && want.every((w) => got.some((q) => near(q[0], w[0]) && near(q[1], w[1])));
    } else {
        const [l, rr] = p.src.split('=');
        const r = solveEquation(parse(l), parse(rr), 'x');
        const want = r.roots.map((x) => x.value);
        const uniq = nums.filter((n, i) => nums.findIndex((m) => near(m, n)) === i);
        ok = uniq.length === want.length && want.every((w) => uniq.some((n) => near(n, w)));
    }
    msg.className = 'es-practice-msg ' + (ok ? 'good' : 'bad');
    msg.textContent = ok ? t('practice_right') : t('practice_wrong');
    ae('practice_check', { ok: ok ? 1 : 0, kind: p.kind });
}

function showPractice() {
    const p = state.practice;
    if (!p) return;
    setMode(p.mode, { keep: true });
    fillFrom(p.src, p.mode);
    refresh(true);
    ae('practice_show', { kind: p.kind });
}

/* ── лист задач (Премиум) ────────────────────────────────────────────────── */
const isPremium = () => !!(window.__tierLimits && window.__tierLimits.is_pro);

function syncPerk() {
    const perk = $('esWsPerk');
    if (perk) perk.hidden = isPremium();
}

function answerOf(g) {
    if (g.mode === 'system') {
        const r = solveSystem(splitSystem(g.src));
        return r.solutions.map((s) => '(' + r.vars.map((v) => s[v].text).join(SEP) + ')').join(', ');
    }
    if (g.mode === 'inequality') {
        const sp = splitInequality(g.src);
        const r = solveInequality(parse(sp.parts[0]), parse(sp.parts[1]), sp.rels[0], 'x', { sep: SEP });
        return 'x ∈ ' + r.text;
    }
    const [l, rr] = g.src.split('=');
    const r = solveEquation(parse(l), parse(rr), 'x');
    return r.roots.map((x) => x.text).join('; ');
}

function makeWorksheet() {
    const kind = currentKind();
    if (!isPremium()) {
        ae('worksheet_locked', { kind });
        if (window.__toast) window.__toast(t('ws_locked').replace(':plan', (CFG.ws && CFG.ws.plan) || ''), 'info');
        if (CFG.ws && CFG.ws.url) setTimeout(() => { window.location.href = CFG.ws.url; }, 900);
        return;
    }
    if (!window.__generatePDF) return;
    const max = (CFG.ws && CFG.ws.max) || 30;
    const count = Math.min(max, parseInt($('esWsCount').value, 10) || 10);
    const variants = Math.max(1, Math.min(4, parseInt($('esWsVariants').value, 10) || 1));
    const withKey = $('esWsKey').checked;
    const base = seed();

    const sheet = document.createElement('div');
    sheet.style.cssText = 'color:#1e293b;font-family:inherit;background:#fff';
    const taskKey = kind.startsWith('system') ? 'ws_task_system' : (kind.startsWith('ineq') ? 'ws_task_inequality' : 'ws_task_equation');
    const keys = [];
    for (let vi = 0; vi < variants; vi++) {
        const seen = new Set();
        const list = [];
        for (let j = 0; list.length < count && j < count * 20; j++) {
            const g = generate(kind, rng(base + vi * 7919 + j * 104729));
            if (seen.has(g.src)) continue;
            seen.add(g.src);
            list.push(g);
        }
        const page = document.createElement('section');
        page.style.cssText = 'padding:0 0 8px;' + (vi ? 'page-break-before:always;break-before:page;' : '');
        page.innerHTML = '<div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;border-bottom:2px solid #1e293b;padding-bottom:6px;margin-bottom:10px">'
            + '<b style="font-size:18px">' + esc(t('ws_heading')) + '</b><span style="font-size:14px">' + esc(t('ws_variant').replace(':n', vi + 1)) + '</span></div>'
            + '<p style="font-size:13px;margin:0 0 6px">' + esc(t('ws_name')) + ': ______________________________</p>'
            + '<p style="font-size:14px;font-weight:700;margin:10px 0">' + esc(t(taskKey)) + '</p>';
        const ol = document.createElement('ol');
        ol.style.cssText = 'columns:2;column-gap:28px;margin:0;padding-left:22px;font-size:15px;line-height:2.4';
        list.forEach((g) => {
            const li = document.createElement('li');
            li.style.cssText = 'break-inside:avoid;font-family:ui-monospace,Menlo,Consolas,monospace;white-space:pre-line';
            li.textContent = g.mode === 'system' ? '{ ' + splitSystem(g.src).map(pretty).join('\n  ') : pretty(g.src);
            ol.appendChild(li);
        });
        page.appendChild(ol);
        sheet.appendChild(page);
        keys.push(list.map(answerOf));
    }
    if (withKey) {
        const key = document.createElement('section');
        key.style.cssText = 'page-break-before:always;break-before:page';
        key.innerHTML = '<b style="font-size:18px;display:block;border-bottom:2px solid #1e293b;padding-bottom:6px;margin-bottom:10px">' + esc(t('ws_key')) + '</b>';
        keys.forEach((answers, vi) => {
            const h = document.createElement('p');
            h.style.cssText = 'font-weight:700;margin:10px 0 4px;font-size:14px';
            h.textContent = t('ws_variant').replace(':n', vi + 1);
            key.appendChild(h);
            const ol = document.createElement('ol');
            ol.style.cssText = 'columns:2;column-gap:28px;margin:0;padding-left:22px;font-size:13px;line-height:1.7';
            answers.forEach((a) => {
                const li = document.createElement('li');
                li.style.cssText = 'break-inside:avoid;font-family:ui-monospace,Menlo,Consolas,monospace';
                li.textContent = loc(a);
                ol.appendChild(li);
            });
            key.appendChild(ol);
        });
        sheet.appendChild(key);
    }
    const btn = $('esWsMake');
    btn.disabled = true;
    window.__generatePDF({
        element: sheet,
        filename: pdfName('worksheet-' + kind),
        strings: { libError: t('ws_error'), genError: t('ws_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        ae('worksheet', { ok: !!(r && r.ok), kind, count, variants, key: withKey ? 1 : 0 });
    }, () => { btn.disabled = false; });
}

/* ── телеметрия ──────────────────────────────────────────────────────────────
 * ⚠️ Сама задача НЕ отправляется — это текст пользователя. Улетает КЛАСС,
 * длина и, при отказе разбора, ОДИН непонятый символ. */
const staged = {};
function stage(name, meta) {
    if (staged[name] && name !== 'error') return;
    staged[name] = 1;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* телеметрия не мешает работе */ }
}
function badChar(code) {
    const m = /^unexpected_char:(.)/.exec(String(code || ''));
    return m ? m[1] : (String(code || '').split(':')[0] || 'parse');
}
const ae = (action, p) => {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, p || {})); } catch (_) { /* телеметрия не мешает работе */ }
};

/* ── снимок: черновик, а не условие ──────────────────────────────────────── */
function bindPhoto() {
    const zone = $('esDrop');
    const st = $('esPhotoState');
    const more = $('esPhotoMore');

    // Браузер, который не потянет чтение, говорит об этом СРАЗУ — до выбора файла.
    if (!ocrAvailable()) {
        st.hidden = false;
        st.textContent = t('photo_unsupported');
        ae('photo_unsupported', {});
    }

    // Кнопка у поля открывает тот же выбор файла (на телефоне — камеру) и
    // раскрывает ящик: состояние чтения человек должен видеть.
    $('esCam').addEventListener('click', () => {
        const box = $('esPhotoBox');
        box.open = true;
        const input = zone && zone.querySelector('input[type=file]');
        if (input) input.click();
        ae('photo_button', {});
    });

    if (!zone || typeof window.__bindUploaderFile !== 'function') return;
    window.__bindUploaderFile(zone, async (file) => {
        if (!file) return;
        $('esPhotoBox').open = true;
        if (!ocrAvailable()) {
            st.hidden = false;
            st.textContent = t('photo_unsupported');
            return;
        }
        more.hidden = true;
        more.innerHTML = '';
        st.hidden = false;
        st.textContent = t('photo_reading');
        ae('photo_start', {});
        try {
            const lines = await readImage(file, { workerUrl: CFG.ocrWorker, lang: 'en' });
            const cands = equationLines(lines);
            if (!cands.length) {
                st.textContent = t('photo_none');
                ae('photo_none', { lines: lines.length });
                return;
            }
            const put = (s) => {
                setMode(isInequality(s) ? 'inequality' : 'equation', { keep: true, auto: true });
                $('esInput').value = s;
                state.fromPhoto = true;
                refresh(true);
            };
            put(cands[0]);
            stage('photo_read', { cands: cands.length });
            // ⚠️ Прочитанное названо прочитанным: человек обязан увидеть, что
            // решается ЕГО уравнение, а не похожее.
            st.textContent = t('photo_read');
            if (cands.length > 1) {
                const label = document.createElement('span');
                label.className = 'tc-hint';
                label.textContent = t('photo_more');
                more.appendChild(label);
                cands.slice(1, 6).forEach((s) => {
                    const b = document.createElement('button');
                    b.type = 'button';
                    b.className = 'tc-chip es-mono';
                    b.textContent = s;
                    b.addEventListener('click', () => { put(s); ae('photo_pick_other', {}); });
                    more.appendChild(b);
                });
                more.hidden = false;
            }
            ae('photo_read', { lines: lines.length, cands: cands.length });
        } catch (e) {
            st.textContent = (e && e.code === 'ocr_unsupported') ? t('photo_unsupported') : t('photo_failed');
            // Причина нужна поддержке: «не удалось» без неё неотличимо от
            // «модель не скачалась» и от «снимок не того формата».
            console.error('[equation-solver] photo:', e);
            ae('photo_failed', { msg: String(e && e.message).slice(0, 80) });
        }
    });
}

/* ── запуск ──────────────────────────────────────────────────────────────── */
function init() {
    const input = $('esInput');
    if (!input) return;

    let timer = 0;
    const onType = () => {
        clearTimeout(timer);
        timer = setTimeout(() => { followInput(); refresh(true); }, 350);
    };
    const onEnter = (e) => { if (e.key === 'Enter') { clearTimeout(timer); followInput(); refresh(true); } };
    [input, $('esSys1'), $('esSys2')].forEach((el) => {
        el.addEventListener('input', onType);
        el.addEventListener('keydown', onEnter);
    });
    $('esSys1').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('esSys2').focus(); });
    let lastField = input;
    [input, $('esSys1'), $('esSys2')].forEach((el) => el.addEventListener('focus', () => { lastField = el; }));

    document.querySelectorAll('[data-es-mode]').forEach((b) => {
        b.addEventListener('click', () => {
            setMode(b.getAttribute('data-es-mode'));
            refresh(true);
        });
    });

    // Вставка знака в МЕСТО КАРЕТКИ, а не в конец: человек правит середину
    // выражения так же часто, как дописывает хвост.
    document.querySelectorAll('[data-es-key]').forEach((b) => {
        b.addEventListener('click', (e) => {
            e.preventDefault();
            const field = state.mode === 'system' ? (lastField === input ? $('esSys1') : lastField) : input;
            const ins = b.getAttribute('data-es-key');
            const a = field.selectionStart == null ? field.value.length : field.selectionStart;
            const z = field.selectionEnd == null ? a : field.selectionEnd;
            field.value = field.value.slice(0, a) + ins + field.value.slice(z);
            const pos = a + ins.length;
            try { field.setSelectionRange(pos, pos); } catch (_) { /* поле без выделения */ }
            field.focus();
            followInput();
            refresh(true);
            ae('key', { k: ins.trim() });
        });
    });

    document.querySelectorAll('[data-es-sample]').forEach((b) => {
        b.addEventListener('click', () => {
            const mode = b.getAttribute('data-es-sample-mode');
            setMode(mode, { keep: true });
            fillFrom(b.getAttribute('data-es-sample'), mode);
            state.fromPhoto = false;
            refresh(true);
        });
    });

    $('esMethods').addEventListener('click', (e) => {
        const b = e.target.closest('[data-es-method]');
        if (b) pickMethod(b.getAttribute('data-es-method'));
    });

    $('esPdf').addEventListener('click', savePdf);
    $('esCopy').addEventListener('click', copySolution);
    if ($('esToSlides')) $('esToSlides').addEventListener('click', toSlides);
    $('esShare').addEventListener('click', share);
    $('esOpenPlot').addEventListener('click', () => {
        const url = plotUrl();
        if (!url) return;
        ae('open_plotter', { mode: state.mode });
        window.location.href = url;
    });

    $('esPracticeNew').addEventListener('click', newPractice);
    $('esPracticeCheck').addEventListener('click', checkPractice);
    $('esPracticeAnswer').addEventListener('keydown', (e) => { if (e.key === 'Enter') checkPractice(); });
    $('esPracticeShow').addEventListener('click', showPractice);
    $('esWsMake').addEventListener('click', makeWorksheet);
    syncPerk();
    ['tier-bootstrap:ready', 'session-bootstrap:ready'].forEach((ev) => {
        window.addEventListener(ev, syncPerk);
        document.addEventListener(ev, syncPerk);
    });

    bindPhoto();

    if (!loadHash()) {
        setMode('equation', { keep: true, force: true });
        input.value = CFG.sample || '';
    }
    refresh(false);

    let raf = 0;
    window.addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => { if (state.parsed) renderPlot(); });
    });

    // Опора для сторожей: состояние решателя видно снаружи без чтения пикселей.
    window.__esState = state;
    window.__esSolve = solveSource;
    window.__esPlotUrl = plotUrl;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
